import { Field, SaveBar, useDraft } from "@/components/hq/useDraft";
import { ImageField } from "@/components/hq/ImageField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";

const inputCls = "rounded-sm bg-[#0A0A0A]";
const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

const Card = ({ children, testId }) => (
  <div data-testid={testId} className="rounded-sm border border-border bg-[#141414] p-4">
    {children}
  </div>
);

const Del = ({ onClick }) => (
  <button onClick={onClick} className="text-muted-foreground hover:text-destructive">
    <Trash2 size={15} />
  </button>
);

/** Retail drink menu — lemonades, dirty sodas, dirty alanis. */
export const MenuEditor = () => {
  const { draft, update, save, dirty } = useDraft("drinkMenu");
  if (!draft) return null;
  const setCat = (id, patch) =>
    update({ ...draft, categories: draft.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const setItem = (c, id, patch) =>
    setCat(c.id, { items: c.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) });

  return (
    <div data-testid="hq-menu">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="drinkMenu"
        addLabel="+ Category"
        onAdd={() =>
          update({
            ...draft,
            categories: [...draft.categories,
              { id: `cat-${Date.now()}`, name: "", tab: "", price: "$8.00", size: "24 oz",
                blurb: "", notice: "", items: [] }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">Drink menu</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        THE TABBED MENU ON /EVENTS. EACH CATEGORY CARRIES ITS OWN PRICE AND CUP SIZE. UNPUBLISH A
        DRINK TO HIDE IT, OR MARK IT UNAVAILABLE TO SHOW IT AS TEMPORARILY OFF. CUSTOMERS PICK UP
        TO FIVE OF THE PUBLISHED DRINKS IN THE BOOKING FORM.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input data-testid="hq-menu-title" value={draft.title || ""}
            onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="INTRO">
          <Input value={draft.intro || ""}
            onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="FOOTNOTE">
            <Input value={draft.note || ""}
              onChange={(e) => update({ ...draft, note: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <Field label="CATERING CTA TITLE">
          <Input data-testid="hq-menu-cta-title" value={draft.cta_title || ""}
            onChange={(e) => update({ ...draft, cta_title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="CATERING CTA BUTTON">
          <Input value={draft.cta_label || ""}
            onChange={(e) => update({ ...draft, cta_label: e.target.value })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="CATERING CTA BODY">
            <Textarea rows={2} value={draft.cta_body || ""}
              onChange={(e) => update({ ...draft, cta_body: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <Field label="CATERING CTA LINK">
          <Input value={draft.cta_href || ""}
            onChange={(e) => update({ ...draft, cta_href: e.target.value })} className={monoCls} />
        </Field>
      </div>

      <div className="mt-6 space-y-4">
        {draft.categories.map((c) => (
          <Card key={c.id} testId={`hq-menu-cat-${c.id}`}>
            <div className="grid gap-3 md:grid-cols-4">
              <Field label="CATEGORY NAME">
                <Input data-testid={`hq-menu-cat-name-${c.id}`} value={c.name || ""}
                  onChange={(e) => setCat(c.id, { name: e.target.value })}
                  className={`${inputCls} display text-lg`} />
              </Field>
              <Field label="TAB LABEL">
                <Input value={c.tab || ""} onChange={(e) => setCat(c.id, { tab: e.target.value })}
                  className={inputCls} />
              </Field>
              <Field label="CUP SIZE">
                <Input value={c.size || ""} onChange={(e) => setCat(c.id, { size: e.target.value })}
                  placeholder="24 oz" className={monoCls} />
              </Field>
              <div className="md:col-span-2">
                <Field label="CATEGORY BLURB">
                  <Textarea rows={2} value={c.blurb || ""}
                    onChange={(e) => setCat(c.id, { blurb: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="NOTICE (E.G. CAFFEINE WARNING)">
                  <Textarea rows={2} value={c.notice || ""}
                    onChange={(e) => setCat(c.id, { notice: e.target.value })} className={inputCls} />
                </Field>
              </div>
            </div>

            <div className="mono mt-5 text-[10px] tracking-widest text-muted-foreground">DRINKS</div>
            <div className="mt-2 space-y-3">
              {(c.items || []).map((it) => (
                <div key={it.id} data-testid={`hq-menu-item-${it.id}`}
                  className="rounded-sm border border-border/60 bg-[#0F0F0F] p-3">
                  <div className="grid gap-3 md:grid-cols-4">
                    <Field label="DRINK NAME">
                      <Input data-testid={`hq-menu-item-name-${it.id}`} value={it.name || ""}
                        onChange={(e) => setItem(c, it.id, { name: e.target.value })}
                        placeholder="Drango" className={inputCls} />
                    </Field>
                    <div className="md:col-span-2">
                      <Field label="INGREDIENTS">
                        <Input value={it.build || ""}
                          onChange={(e) => setItem(c, it.id, { build: e.target.value })}
                          placeholder="Mango, dragonfruit and lemonade" className={inputCls} />
                      </Field>
                    </div>
                    <Field label="BADGE">
                      <Input data-testid={`hq-menu-item-badge-${it.id}`} value={it.badge || ""}
                        onChange={(e) => setItem(c, it.id, { badge: e.target.value })}
                        placeholder="CUSTOMER FAVORITE" className={monoCls} />
                    </Field>
                    <Field label="SORT ORDER">
                      <Input type="number" value={it.order ?? 0}
                        onChange={(e) => setItem(c, it.id, { order: Number(e.target.value) })}
                        className={monoCls} />
                    </Field>
                    <div className="md:col-span-2">
                      <ImageField
                        label="DRINK PHOTO"
                        testId={`hq-menu-item-image-${it.id}`}
                        value={it.image || ""}
                        onChange={(url) => setItem(c, it.id, { image: url })}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <button
                      data-testid={`hq-menu-item-publish-${it.id}`}
                      onClick={() => setItem(c, it.id, { published: it.published === false })}
                      className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-primary hover:border-primary"
                    >
                      {it.published === false ? "PUBLISH" : "UNPUBLISH"}
                    </button>
                    <button
                      data-testid={`hq-menu-item-available-${it.id}`}
                      onClick={() => setItem(c, it.id, { available: it.available === false })}
                      className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-muted-foreground hover:border-primary hover:text-primary"
                    >
                      {it.available === false ? "MARK AVAILABLE" : "MARK UNAVAILABLE"}
                    </button>
                    <span className="mono text-[10px] tracking-widest text-muted-foreground">
                      {it.published === false ? "HIDDEN FROM THE SITE" : "LIVE"}
                    </span>
                    <div className="ml-auto">
                      <Del onClick={() => setCat(c.id, { items: c.items.filter((x) => x.id !== it.id) })} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-4">
              <button
                data-testid={`hq-menu-add-item-${c.id}`}
                onClick={() => setCat(c.id, {
                  items: [...(c.items || []),
                    { id: `dm-${Date.now()}`, name: "", build: "", price: "", badge: "",
                      image: "", published: true, available: true,
                      order: (c.items || []).length + 1 }],
                })}
                className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-primary hover:border-primary"
              >
                + DRINK
              </button>
              <button
                onClick={() => update({ ...draft, categories: draft.categories.filter((x) => x.id !== c.id) })}
                className="mono text-[10px] tracking-widest text-muted-foreground hover:text-destructive"
              >
                DELETE CATEGORY
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

/** Drink packages, service options, upgrades and the booking fine print. */
export const CateringPackagesEditor = () => {
  const { draft, update, save, dirty } = useDraft("cateringPackages");
  if (!draft) return null;
  const setIn = (list, id, patch) =>
    update({ ...draft, [list]: draft[list].map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  const lines = (key) => (
    <Textarea
      data-testid={`hq-cp-${key}`}
      rows={key === "booking_info" ? 9 : 6}
      value={(draft[key] || []).join("\n")}
      onChange={(e) => update({ ...draft, [key]: e.target.value.split("\n") })}
      className={inputCls}
    />
  );

  return (
    <div data-testid="hq-catering-packages">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="cateringPackages"
        addLabel="+ Package"
        onAdd={() =>
          update({
            ...draft,
            packages: [...draft.packages,
              { id: `pk-${Date.now()}`, name: "", drinks: "", drink_count: 0, price: "", price_amount: 0, badge: "" }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">Catering packages & service</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        DRINK PACKAGES ARE STEP ONE, SERVICE OPTIONS ARE STEP TWO — THEY ADD TOGETHER IN THE QUOTE
        CALCULATOR. THE NUMBER FIELDS DRIVE THE MATH; THE TEXT FIELDS ARE WHAT CUSTOMERS READ.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input data-testid="hq-cp-title" value={draft.title || ""}
            onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="INTRO">
          <Input value={draft.intro || ""}
            onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
        </Field>
        <Field label="STEP 1 HEADING">
          <Input value={draft.step_one || ""}
            onChange={(e) => update({ ...draft, step_one: e.target.value })} className={inputCls} />
        </Field>
        <Field label="STEP 2 HEADING">
          <Input value={draft.step_two || ""}
            onChange={(e) => update({ ...draft, step_two: e.target.value })} className={inputCls} />
        </Field>
      </div>

      <div className="mono mt-6 text-[10px] tracking-widest text-muted-foreground">
        DRINK PACKAGES — NAME · DRINKS LABEL · DRINK COUNT · PRICE LABEL · PRICE · BADGE
      </div>
      <div className="mt-2 space-y-2">
        {draft.packages.map((p) => (
          <div key={p.id} data-testid={`hq-package-${p.id}`} className="flex items-start gap-2">
            <div className="flex-1">
              <Input data-testid={`hq-package-name-${p.id}`} value={p.name || ""}
                onChange={(e) => setIn("packages", p.id, { name: e.target.value })}
                placeholder="Big Smash" className={inputCls} />
            </div>
            <div className="flex-1">
              <Input value={p.drinks || ""} onChange={(e) => setIn("packages", p.id, { drinks: e.target.value })}
                placeholder="150 drinks" className={inputCls} />
            </div>
            <div className="w-24">
              <Input data-testid={`hq-package-count-${p.id}`} type="number" value={p.drink_count ?? 0}
                onChange={(e) => setIn("packages", p.id, { drink_count: Number(e.target.value) })}
                className={monoCls} />
            </div>
            <div className="w-28">
              <Input value={p.price || ""} onChange={(e) => setIn("packages", p.id, { price: e.target.value })}
                placeholder="$750" className={inputCls} />
            </div>
            <div className="w-24">
              <Input data-testid={`hq-package-amount-${p.id}`} type="number" value={p.price_amount ?? 0}
                onChange={(e) => setIn("packages", p.id, { price_amount: Number(e.target.value) })}
                className={monoCls} />
            </div>
            <div className="w-32">
              <Input value={p.badge || ""} onChange={(e) => setIn("packages", p.id, { badge: e.target.value })}
                placeholder="MOST POPULAR" className={monoCls} />
            </div>
            <div className="mt-2">
              <Del onClick={() => update({ ...draft, packages: draft.packages.filter((x) => x.id !== p.id) })} />
            </div>
          </div>
        ))}
      </div>
      <p className="mono mt-2 text-[10px] leading-relaxed text-muted-foreground">
        SET THE PRICE NUMBER TO 0 FOR A "CUSTOM QUOTE" TIER.
      </p>

      <div className="mono mt-6 text-[10px] tracking-widest text-muted-foreground">
        SERVICE OPTIONS
      </div>
      <div className="mt-2 space-y-3">
        {(draft.services || []).map((s) => (
          <Card key={s.id} testId={`hq-service-${s.id}`}>
            <div className="grid gap-3 md:grid-cols-4">
              <Field label="NAME">
                <Input data-testid={`hq-service-name-${s.id}`} value={s.name || ""}
                  onChange={(e) => setIn("services", s.id, { name: e.target.value })}
                  className={`${inputCls} display text-lg`} />
              </Field>
              <Field label="PRICE LABEL">
                <Input value={s.price_label || ""}
                  onChange={(e) => setIn("services", s.id, { price_label: e.target.value })} className={inputCls} />
              </Field>
              <Field label="FEE ($)">
                <Input data-testid={`hq-service-fee-${s.id}`} type="number" value={s.fee ?? 0}
                  onChange={(e) => setIn("services", s.id, { fee: Number(e.target.value) })} className={monoCls} />
              </Field>
              <Field label="MIN DRINKS">
                <Input type="number" value={s.min_drinks ?? 0}
                  onChange={(e) => setIn("services", s.id, { min_drinks: Number(e.target.value) })} className={monoCls} />
              </Field>
              <div className="md:col-span-3">
                <Field label="DESCRIPTION">
                  <Textarea rows={2} value={s.blurb || ""}
                    onChange={(e) => setIn("services", s.id, { blurb: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <ImageField
                  label="SERVICE PHOTO"
                  testId={`hq-service-image-${s.id}`}
                  value={s.image || ""}
                  onChange={(url) => setIn("services", s.id, { image: url })}
                />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mono mt-6 text-[10px] tracking-widest text-muted-foreground">UPGRADES</div>
      <div className="mt-2 space-y-2">
        {(draft.upgrades || []).map((u) => (
          <div key={u.id} data-testid={`hq-upgrade-${u.id}`} className="flex items-start gap-2">
            <div className="flex-[1.5]">
              <Input value={u.label || ""} onChange={(e) => setIn("upgrades", u.id, { label: e.target.value })}
                placeholder="Dirty Alani energy drinks" className={inputCls} />
            </div>
            <div className="flex-1">
              <Input value={u.price_note || ""}
                onChange={(e) => setIn("upgrades", u.id, { price_note: e.target.value })}
                placeholder="+$1 per drink" className={inputCls} />
            </div>
            <div className="w-24">
              <Input data-testid={`hq-upgrade-amount-${u.id}`} type="number" value={u.amount ?? 0}
                onChange={(e) => setIn("upgrades", u.id, { amount: Number(e.target.value) })} className={monoCls} />
            </div>
            <div className="mt-2">
              <Del onClick={() => update({ ...draft, upgrades: draft.upgrades.filter((x) => x.id !== u.id) })} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Field label="PER-DRINK NOTE">
          <Input value={draft.per_drink_note || ""}
            onChange={(e) => update({ ...draft, per_drink_note: e.target.value })} className={inputCls} />
        </Field>
        <Field label="DEPOSIT (%)">
          <Input data-testid="hq-cp-deposit" type="number" value={draft.deposit_pct ?? 50}
            onChange={(e) => update({ ...draft, deposit_pct: Number(e.target.value) })} className={monoCls} />
        </Field>
        <Field label="WHAT'S INCLUDED (ONE PER LINE)">{lines("includes")}</Field>
        <Field label="BOOKING INFORMATION (ONE PER LINE)">{lines("booking_info")}</Field>
        <Field label="QUOTE HEADING">
          <Input value={draft.quote_title || ""}
            onChange={(e) => update({ ...draft, quote_title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="QUOTE FOOTNOTE">
          <Input value={draft.quote_note || ""}
            onChange={(e) => update({ ...draft, quote_note: e.target.value })} className={inputCls} />
        </Field>
      </div>
    </div>
  );
};

/** Instagram wall shown on /events — embeds, no file uploads needed. */
export const InstagramEditor = () => {
  const { draft, update, save, dirty } = useDraft("instagramWall");
  if (!draft) return null;
  const setPost = (id, patch) =>
    update({ ...draft, posts: draft.posts.map((p) => (p.id === id ? { ...p, ...patch } : p)) });

  return (
    <div data-testid="hq-instagram">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="instagramWall"
        addLabel="+ Post"
        onAdd={() =>
          update({
            ...draft,
            posts: [...(draft.posts || []),
              { id: `ig-${Date.now()}`, url: "", caption: "", thumbnail: "" }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">Instagram wall</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        OPEN A POST ON INSTAGRAM, HIT SHARE → COPY LINK, PASTE IT HERE. PHOTO POSTS
        (INSTAGRAM.COM/P/…) AND REELS (INSTAGRAM.COM/REEL/…) BOTH WORK. INSTAGRAM SERVES THE
        MEDIA, SO NOTHING NEEDS UPLOADING.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input data-testid="hq-ig-title" value={draft.title || ""}
            onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="HANDLE">
          <Input data-testid="hq-ig-handle" value={draft.handle || ""}
            onChange={(e) => update({ ...draft, handle: e.target.value })} className={monoCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="INTRO">
            <Textarea rows={2} value={draft.intro || ""}
              onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <Field label="PROFILE URL">
          <Input value={draft.handle_url || ""}
            onChange={(e) => update({ ...draft, handle_url: e.target.value.trim() })} className={monoCls} />
        </Field>
        <Field label="BUTTON LABEL">
          <Input value={draft.cta || ""}
            onChange={(e) => update({ ...draft, cta: e.target.value })} className={inputCls} />
        </Field>
      </div>

      <div className="mt-6 space-y-2">
        {(draft.posts || []).map((p) => (
          <div key={p.id} data-testid={`hq-ig-post-${p.id}`} className="flex items-start gap-3">
            <div className="flex-[1.5]">
              <Input data-testid={`hq-ig-url-${p.id}`} value={p.url || ""}
                onChange={(e) => setPost(p.id, { url: e.target.value.trim() })}
                placeholder="https://www.instagram.com/p/XXXXXXXX/" className={monoCls} />
            </div>
            <div className="flex-1">
              <Input data-testid={`hq-ig-caption-${p.id}`} value={p.caption || ""}
                onChange={(e) => setPost(p.id, { caption: e.target.value })}
                placeholder="Caption (optional)" className={inputCls} />
            </div>
            <div className="flex-1">
              <ImageField
                label=""
                testId={`hq-ig-thumb-${p.id}`}
                preview={false}
                value={p.thumbnail || ""}
                onChange={(url) => setPost(p.id, { thumbnail: url })}
                placeholder="/brand/cover.jpg (fallback)"
              />
            </div>
            <div className="mt-2">
              <Del onClick={() => update({ ...draft, posts: draft.posts.filter((x) => x.id !== p.id) })} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Event photos and the catering FAQ. */
export const GalleryFaqEditor = () => {
  const gallery = useDraft("eventGallery");
  const faq = useDraft("cateringFaq");
  if (!gallery.draft || !faq.draft) return null;

  return (
    <div data-testid="hq-gallery-faq">
      <SaveBar
        dirty={gallery.dirty}
        save={gallery.save}
        testId="eventGallery"
        addLabel="+ Photo"
        onAdd={() =>
          gallery.update({
            ...gallery.draft,
            photos: [...gallery.draft.photos,
              { id: `ph-${Date.now()}`, image: "", caption: "", tag: "EVENT" }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">Event photos</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        UPLOAD STRAIGHT FROM YOUR PHONE OR COMPUTER WITH THE UPLOAD BUTTON ON EACH ROW. REPLACE THE
        PLACEHOLDERS WITH YOUR OWN WEDDING, CORPORATE, SCHOOL AND FESTIVAL SHOTS.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input data-testid="hq-gallery-title" value={gallery.draft.title || ""}
            onChange={(e) => gallery.update({ ...gallery.draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="INTRO">
          <Input value={gallery.draft.intro || ""}
            onChange={(e) => gallery.update({ ...gallery.draft, intro: e.target.value })} className={inputCls} />
        </Field>
      </div>

      <div className="mt-4 space-y-2">
        {gallery.draft.photos.map((p) => (
          <div key={p.id} data-testid={`hq-photo-${p.id}`} className="flex items-start gap-3">
            {p.image ? (
              <img src={p.image} alt="" className="h-12 w-12 shrink-0 border border-border object-cover" />
            ) : null}
            <div className="flex-[1.4]">
              <ImageField
                label=""
                testId={`hq-photo-image-${p.id}`}
                preview={false}
                value={p.image || ""}
                onChange={(url) => gallery.update({
                  ...gallery.draft,
                  photos: gallery.draft.photos.map((x) => (x.id === p.id ? { ...x, image: url } : x)),
                })}
                placeholder="/brand/my-event.jpg"
              />
            </div>
            <div className="flex-1">
              <Input data-testid={`hq-photo-caption-${p.id}`} value={p.caption || ""}
                onChange={(e) => gallery.update({
                  ...gallery.draft,
                  photos: gallery.draft.photos.map((x) => (x.id === p.id ? { ...x, caption: e.target.value } : x)),
                })}
                placeholder="Caption" className={inputCls} />
            </div>
            <div className="w-28">
              <Input value={p.tag || ""}
                onChange={(e) => gallery.update({
                  ...gallery.draft,
                  photos: gallery.draft.photos.map((x) => (x.id === p.id ? { ...x, tag: e.target.value } : x)),
                })}
                placeholder="TAG" className={monoCls} />
            </div>
            <div className="mt-2">
              <Del onClick={() => gallery.update({
                ...gallery.draft,
                photos: gallery.draft.photos.filter((x) => x.id !== p.id),
              })} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 border-t border-border pt-6">
        <SaveBar
          dirty={faq.dirty}
          save={faq.save}
          testId="cateringFaq"
          addLabel="+ Question"
          onAdd={() =>
            faq.update({ ...faq.draft, items: [...faq.draft.items, { id: `cf-${Date.now()}`, q: "", a: "" }] })
          }
        />
        <h3 className="display text-2xl uppercase">Catering FAQ</h3>
        <div className="mt-5">
          <Field label="SECTION TITLE">
            <Input value={faq.draft.title || ""}
              onChange={(e) => faq.update({ ...faq.draft, title: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <div className="mt-4 space-y-3">
          {faq.draft.items.map((item) => (
            <Card key={item.id} testId={`hq-cfaq-${item.id}`}>
              <Field label="QUESTION">
                <Input data-testid={`hq-cfaq-q-${item.id}`} value={item.q || ""}
                  onChange={(e) => faq.update({
                    ...faq.draft,
                    items: faq.draft.items.map((x) => (x.id === item.id ? { ...x, q: e.target.value } : x)),
                  })}
                  className={inputCls} />
              </Field>
              <div className="mt-3">
                <Field label="ANSWER">
                  <Textarea data-testid={`hq-cfaq-a-${item.id}`} rows={3} value={item.a || ""}
                    onChange={(e) => faq.update({
                      ...faq.draft,
                      items: faq.draft.items.map((x) => (x.id === item.id ? { ...x, a: e.target.value } : x)),
                    })}
                    className={inputCls} />
                </Field>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => faq.update({
                    ...faq.draft, items: faq.draft.items.filter((x) => x.id !== item.id),
                  })}
                  className="mono text-[10px] tracking-widest text-muted-foreground hover:text-destructive"
                >
                  DELETE QUESTION
                </button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
